import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import api from "../../store/services/api";

// MATERIAL UI COMPONENTS
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

// ICONS
import AddIcon from "@mui/icons-material/Add";
import StorefrontIcon from "@mui/icons-material/Storefront";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SortIcon from "@mui/icons-material/Sort";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import SortByAlphaIcon from "@mui/icons-material/SortByAlpha";

// APP COMPONENTS
import RemoveModal from "../modals/removeModal";
import MerchantCard from "./merchantCard";
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../modals/AnimatedModal";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
  exit: {
    y: -20,
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
  },
  tap: {
    scale: 0.95,
  },
};

export default function MerchantsIndex({ show, closeModal }) {
  const theme = useTheme();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [filteredMerchants, setFilteredMerchants] = useState([]);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [removeLabel, setRemoveLabel] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [sortBy, setSortBy] = useState("orders"); // Default sort by orders

  useEffect(() => {
    if (show) {
      loadData();
    }
  }, [show]);

  // Filter and sort merchants when search term or sort method changes
  useEffect(() => {
    let results = [...merchants];

    // Apply search filter if search term exists
    if (searchTerm.trim() !== "") {
      results = results.filter((merchant) =>
        merchant.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    results = sortMerchants(results, sortBy);

    setFilteredMerchants(results);
  }, [searchTerm, merchants, sortBy]);

  // Sort merchants based on selected sort method
  const sortMerchants = (merchantsToSort, sortMethod) => {
    if (sortMethod === "orders") {
      // Sort by todays_order_count from highest to lowest
      return [...merchantsToSort].sort(
        (a, b) => (b.todays_order_count || 0) - (a.todays_order_count || 0)
      );
    } else if (sortMethod === "name") {
      // Sort alphabetically by name
      return [...merchantsToSort].sort((a, b) => a.name.localeCompare(b.name));
    }
    return merchantsToSort;
  };

  const handleSortChange = (event, newSortBy) => {
    if (newSortBy !== null) {
      setSortBy(newSortBy);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      await getRestaurants();
    } catch (err) {
      setError("Erro ao carregar lojas. Tente novamente.");
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const getRestaurants = async () => {
    try {
      setRefreshing(true);
      const res = await api.get("/merchant");
      const newRestaurants = res.data;

      const _merchants = merchants.map((r) => {
        const found = newRestaurants.find((nr) => r.id === nr.id);
        return found
          ? {
              ...r,
              status: found.status,
              title: found.title,
              reason: found.reason,
              todays_order_count: found.todays_order_count || 0,
            }
          : r;
      });

      const new_rests = newRestaurants.filter(
        ({ id: id1 }) => !merchants.some(({ id: id2 }) => id2 === id1)
      );
      const result = [...new_rests, ..._merchants];

      // Apply default sorting before setting state
      const sortedResult = sortMerchants(result, sortBy);

      setMerchants(result);
      setFilteredMerchants(sortedResult);
    } catch (err) {
      console.error("ERROR GET RESTAURANTS", err);
      setError("Não foi possível carregar suas lojas. Tente novamente.");
      throw err;
    } finally {
      setRefreshing(false);
    }
  };

  const toggleRemoveModal = (merchant) => {
    setShowRemoveModal(!showRemoveModal);
    if (merchant) {
      setRemoveLabel(merchant.name);
      setSelectedId(merchant.id);
    }
  };

  const toggleEditModal = () => {
    history.push(`/restaurant/new`);
    closeModal();
  };

  const goToEdit = (merchant) => {
    history.push(`/restaurant/${merchant.id}`);
    closeModal();
  };

  const removeDataRestaurant = async () => {
    try {
      setLoading(true);
      await api.delete(`/merchant?rid=${selectedId}`);
      await getRestaurants();
      setShowRemoveModal(false);
    } catch (err) {
      console.error("Error removing restaurant:", err);
      setError("Erro ao remover loja. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadData();
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  // Skeleton loaders for loading state
  const renderSkeletons = () => {
    return Array.from({ length: 6 }).map((_, index) => (
      <Grid item xs={12} sm={6} md={4} key={`skeleton-${index}`}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={200}
          sx={{ borderRadius: 2 }}
        />
      </Grid>
    ));
  };

  // Empty state when no merchants are found
  const renderEmptyState = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
        px: 3,
        textAlign: "center",
        width: "100%",
      }}
    >
      {searchTerm ? (
        <>
          <SearchIcon
            sx={{
              fontSize: 64,
              color: alpha(theme.palette.text.secondary, 0.3),
              mb: 2,
            }}
          />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Nenhum resultado encontrado
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ maxWidth: 400, mb: 3 }}
          >
            Não encontramos lojas correspondentes a "{searchTerm}"
          </Typography>
          <motion.div
            whileHover="hover"
            whileTap="tap"
            variants={buttonVariants}
          >
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              sx={{ borderRadius: 2 }}
            >
              Limpar Busca
            </Button>
          </motion.div>
        </>
      ) : (
        <>
          <StorefrontIcon
            sx={{
              fontSize: 64,
              color: alpha(theme.palette.text.secondary, 0.3),
              mb: 2,
            }}
          />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Nenhuma loja cadastrada
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ maxWidth: 400, mb: 3 }}
          >
            Cadastre sua primeira loja para começar a gerenciar seus produtos e
            serviços
          </Typography>
          <motion.div
            whileHover="hover"
            whileTap="tap"
            variants={buttonVariants}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => toggleEditModal()}
              color="primary"
              sx={{ borderRadius: 2 }}
            >
              Adicionar Nova Loja
            </Button>
          </motion.div>
        </>
      )}
    </Box>
  );

  return (
    <>
      <AnimatedModal
        isOpen={show && !showEditModal}
        onClose={closeModal}
        width="85%"
        maxWidth="1400px"
        maxHeight="90vh"
      >
        <ModalHeader
          title="Suas Lojas"
          icon={StorefrontIcon}
          onClose={closeModal}
          actions={[
            <Button
              key="add-store"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => toggleEditModal()}
              sx={{ borderRadius: 2 }}
            >
              Nova Loja
            </Button>,
          ]}
        />

        <ModalContent>
          {/* Error message display */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{ mb: 3, borderRadius: 2 }}
              variant="filled"
              icon={<ErrorOutlineIcon />}
            >
              {error}
            </Alert>
          )}

          {/* Search and actions bar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Chip
                label={`${filteredMerchants.length} ${
                  filteredMerchants.length === 1 ? "loja" : "lojas"
                }`}
                color="primary"
                sx={{
                  borderRadius: 1.5,
                  fontWeight: "medium",
                }}
              />

              {/* Sort Options */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  ml: 1,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: 2,
                  pl: 1,
                  backgroundColor: alpha(theme.palette.background.paper, 0.7),
                }}
              >
                <SortIcon
                  fontSize="small"
                  sx={{
                    color: theme.palette.text.secondary,
                    mr: 0.5,
                  }}
                />
                <ToggleButtonGroup
                  value={sortBy}
                  exclusive
                  onChange={handleSortChange}
                  aria-label="ordenar lojas"
                  size="small"
                  sx={{
                    "& .MuiToggleButtonGroup-grouped": {
                      border: 0,
                      "&.Mui-selected": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <ToggleButton
                    value="orders"
                    aria-label="pedidos de hoje"
                    sx={{ borderRadius: 1.5, py: 0.5, px: 1 }}
                  >
                    <Tooltip title="Ordenar por pedidos de hoje">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <LocalFireDepartmentIcon
                          fontSize="small"
                          sx={{ mr: 0.5 }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ display: { xs: "none", sm: "block" } }}
                        >
                          Pedidos
                        </Typography>
                      </Box>
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton
                    value="name"
                    aria-label="ordem alfabética"
                    sx={{ borderRadius: 1.5, py: 0.5, px: 1 }}
                  >
                    <Tooltip title="Ordenar alfabeticamente">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <SortByAlphaIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Typography
                          variant="caption"
                          sx={{ display: { xs: "none", sm: "block" } }}
                        >
                          A-Z
                        </Typography>
                      </Box>
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {/* Search Field */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                <Box
                  component="input"
                  placeholder="Buscar loja..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.2
                    )}`,
                    borderRadius: 2,
                    py: 1,
                    px: 2,
                    pl: 4,
                    width: { xs: 140, sm: 200 },
                    outline: "none",
                    "&:focus": {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 2px ${alpha(
                        theme.palette.primary.main,
                        0.2
                      )}`,
                    },
                    fontSize: "0.875rem",
                  }}
                />
                <SearchIcon
                  sx={{
                    position: "absolute",
                    left: 10,
                    color: theme.palette.text.secondary,
                    pointerEvents: "none",
                  }}
                />

                {searchTerm && (
                  <Tooltip title="Limpar busca">
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      style={{ position: "absolute", right: 10 }}
                    >
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        sx={{ p: 0.5 }}
                      >
                        ×
                      </IconButton>
                    </motion.div>
                  </Tooltip>
                )}
              </Box>

              {/* Refresh Button */}
              <Tooltip title="Atualizar lista">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <IconButton
                    onClick={handleRefresh}
                    disabled={loading || refreshing}
                    sx={{
                      color: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                      },
                    }}
                  >
                    {refreshing ? (
                      <Skeleton variant="circular" width={24} height={24} />
                    ) : (
                      <RefreshIcon />
                    )}
                  </IconButton>
                </motion.div>
              </Tooltip>
            </Box>
          </Box>

          {/* Main content - merchants grid or loading/empty states */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.background.paper, 0.5),
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              height: "100%",
            }}
          >
            {loading ? (
              // Loading skeletons
              <Grid container spacing={3}>
                {renderSkeletons()}
              </Grid>
            ) : filteredMerchants.length === 0 ? (
              // Empty state
              renderEmptyState()
            ) : (
              // Merchants grid
              <Grid
                container
                spacing={3}
                component={motion.div}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                sx={{ pb: 2 }}
              >
                <AnimatePresence>
                  {filteredMerchants.map((merchant) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      key={merchant.id}
                      component={motion.div}
                      variants={itemVariants}
                      exit="exit"
                      layout
                    >
                      <MerchantCard
                        name={merchant.name}
                        merchant={merchant}
                        editData={goToEdit}
                        removeData={toggleRemoveModal}
                        ordersCount={merchant.todays_order_count || 0}
                      />
                    </Grid>
                  ))}
                </AnimatePresence>
              </Grid>
            )}
          </Paper>
        </ModalContent>

        <ModalFooter>
          <Typography variant="caption" color="textSecondary">
            Última atualização: {new Date().toLocaleTimeString()}
          </Typography>
        </ModalFooter>
      </AnimatedModal>

      {/* Remove Modal */}
      <RemoveModal
        title="Deletar Loja"
        toggleModal={toggleRemoveModal}
        onRemove={removeDataRestaurant}
        show={showRemoveModal}
        message={`Tem certeza que deseja deletar a loja "${removeLabel}"?`}
      />
    </>
  );
}
